/**
 * Helper para generar URLs optimizadas de Cloudinary
 */

interface CloudinaryOptions {
	width?: number;
	height?: number;
	quality?: 'auto' | 'auto:best' | 'auto:good' | 'auto:eco' | 'auto:low' | number;
	format?: 'auto' | 'webp' | 'avif' | 'png' | 'jpg';
	crop?: 'scale' | 'fit' | 'limit' | 'fill' | 'thumb';
	gravity?: 'auto' | 'center' | 'face' | 'faces';
	dpr?: 'auto' | number;
	flags?: string[];
}

const CLOUDINARY_CLOUD_NAME = 'dlqfj0qal';
const CLOUDINARY_BASE_URL = `https://res.cloudinary.com/${CLOUDINARY_CLOUD_NAME}/image/upload`;

/**
 * Genera una URL optimizada de Cloudinary
 * @param publicId - El ID público de la imagen en Cloudinary (sin extensión)
 * @param options - Opciones de transformación
 * @returns URL optimizada
 */
export function getCloudinaryUrl(publicId: string, options: CloudinaryOptions = {}): string {
	const {
		width,
		height,
		quality = 'auto',
		format = 'auto',
		crop = 'limit',
		gravity = 'auto',
		dpr = 'auto',
		flags = []
	} = options;

	// Construir transformaciones
	const transformations: string[] = [];

	// Formato y calidad
	transformations.push(`f_${format}`);
	transformations.push(`q_${quality}`);

	// Dimensiones
	if (width) transformations.push(`w_${width}`);
	if (height) transformations.push(`h_${height}`);

	// Crop y gravity
	transformations.push(`c_${crop}`);
	if (crop === 'fill' || crop === 'thumb') {
		transformations.push(`g_${gravity}`);
	}

	// DPR para retina displays
	transformations.push(`dpr_${dpr}`);

	// Flags adicionales
	if (flags.length > 0) {
		transformations.push(`fl_${flags.join('.')}`);
	}

	// Construir URL final
	const transformation = transformations.join(',');
	return `${CLOUDINARY_BASE_URL}/${transformation}/${publicId}`;
}

/**
 * Genera URL optimizada para logos (SVG preferido, PNG fallback)
 */
export function getCloudinaryLogoUrl(logoName: string): string {
	// Si el logoName ya incluye version ID (v1234567/), usarlo directamente
	if (logoName.includes('/')) {
		return getCloudinaryUrl(logoName, {
			format: 'auto',
			quality: 'auto:best',
			width: 200,
			crop: 'limit',
			flags: ['preserve_transparency']
		});
	}
	// Si no, usar el formato antiguo para compatibilidad
	return getCloudinaryUrl(`talleres-santa-clara/logos/${logoName}`, {
		format: 'auto',
		quality: 'auto:best',
		width: 200,
		crop: 'limit',
		flags: ['preserve_transparency']
	});
}

/**
 * Genera URL optimizada para imágenes de brands/marcas
 */
export function getCloudinaryBrandUrl(brandName: string): string {
	// Si el brandName ya incluye version ID (v1234567/), usarlo directamente
	if (brandName.includes('/')) {
		return getCloudinaryUrl(brandName, {
			format: 'auto',
			quality: 'auto',
			width: 150,
			height: 100,
			crop: 'fit',
			flags: ['preserve_transparency']
		});
	}
	// Si no, usar el formato antiguo para compatibilidad
	return getCloudinaryUrl(`talleres-santa-clara/brands/${brandName}`, {
		format: 'auto',
		quality: 'auto',
		width: 150,
		height: 100,
		crop: 'fit',
		flags: ['preserve_transparency']
	});
}

/**
 * Helper para migración gradual de imágenes locales
 * Mapea paths locales a IDs de Cloudinary
 */
export const IMAGE_MIGRATION_MAP: Record<string, string> = {
	// Logos principales
	'/imgs/logotipo.png': 'v1751842235/logotipo.png',
	'/imgs/medios-pago.png': 'v1751842234/medios-pago.png',

	// Brands
	'/imgs/brands/chevrolet.png': 'v1751842169/chevrolet.png',
	'/imgs/brands/hyundai.png': 'v1751842168/hyundai.png',

	// Iconos PWA (estos pueden quedarse locales por ahora)
	'/android-chrome-192x192.png': 'local',
	'/android-chrome-512x512.png': 'local',
	'/mstile-150x150.png': 'local'
};

/**
 * Obtiene la URL de Cloudinary si la imagen está migrada,
 * o devuelve la URL local si no
 */
export function getOptimizedImageUrl(localPath: string, options?: CloudinaryOptions): string {
	const cloudinaryId = IMAGE_MIGRATION_MAP[localPath];

	if (cloudinaryId && cloudinaryId !== 'local') {
		return getCloudinaryUrl(cloudinaryId, options);
	}

	// Fallback a la imagen local
	return localPath;
}
